import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'
import { setScheduleFilters, setShowPopupScheduleFilters } from '../../../../store/slices/modalSlice'
import { type RootState } from '../../../../store/store'
import { REACT_APP_BSO_USER_ID } from '../../../../urls'
import { type Unit } from '../../../../store/slices/unitsSlice'
import { DEFAULT_SCHEDULE_FILTERS, type ScheduleFiltersState } from './scheduleFilters.types'

export interface ScheduleFiltersVMProps {
  scheduleFiltersApply: boolean
  setScheduleFiltersApply: Dispatch<SetStateAction<boolean>>
}

export const STATUS_PHASES = [
  {
    name: 'Documentation',
    description: 'The stage where client documents are required or need resubmission.',
    statuses: ['Client docs required', 'Resubmit required']
  },
  {
    name: 'Under review',
    description: 'The initial stage where the deal is being processed.',
    statuses: [
      'In-process',
      'Sent to landlord',
      'Approved',
      'Negotiated by other agent',
      'Negotiated by agent',
      'Negotiated by BSO',
      'Negotiated by landlord',
      'Pending BSO admin confirmation'
    ]
  },
  {
    name: 'Conformations & Finalization',
    description: 'The final steps before completion, including payment approvals, form submissions, and other formalities.',
    statuses: [
      'Confirmed',
      'Waiting for slip approval',
      'Slip approved',
      'Slip rejected',
      'Signing F form',
      'Signing other documents',
      'Obtaining NOC',
      'Downpayment',
      'Transfer process',
      'Waiting for updates',
      'Waiting booking form send',
      'Waiting booking form sign',
      'F Form requested',
      'Manual contract requested',
      'Waiting draft contract send',
      'Waiting draft contract approve',
      'Draft contract approved',
      'F Form sent',
      'Manual contract sent',
      'F Form signed',
      'Manual contract signed'
    ]
  },
  {
    name: 'Closure',
    description: 'The stage where the deal is closed either completed.',
    statuses: ['Completed', 'Rejected', 'Canceled', 'Canceled by BSO', 'Canceled by yourself', 'Canceled by agent', 'Canceled by other agent']
  }
]

export const VIEWING_PHASES = [
  {
    name: 'Processing',
    description: 'Viewing request is awaiting approval.',
    statuses: ['In-process', 'Rescheduled by agent', 'Rescheduled by other agent', 'Rescheduled by BSO']
  },
  {
    name: 'Viewing & Feedback',
    description: 'Viewing approved. Pick up the keys and proceed with the viewing.',
    statuses: ['Approved', 'Waiting for feedback']
  },
  {
    name: 'Closure',
    description: 'The stage where the viewing is closed as rejected, canceled  or completed.',
    statuses: ['Completed', 'Rejected', 'Canceled', 'Canceled by BSO', 'Canceled by agent', 'Canceled by other agent', 'Waiting for complain']
  }
]

export const ADS_PHASES = [
  {
    name: 'Document Processing',
    description: 'Viewing request is awaiting approval.',
    statuses: ['In-process', 'Docs received', 'A Form added', 'A Form rejected']
  },
  {
    name: 'Ads Review & Active',
    description: 'Viewing approved. Pick up the keys and proceed with the viewing.',
    statuses: ['Links added', 'Links rejected', 'Links approved', 'Ads removing rejected', 'Ads removed', 'Confirmed', 'Advertisement closed']
  },
  {
    name: 'Ads Closure',
    description: 'The stage where the viewing is closed as rejected, canceled  or completed.',
    statuses: ['Completed', 'Canceled']
  }
]

export function useScheduleFiltersVM({ scheduleFiltersApply, setScheduleFiltersApply }: ScheduleFiltersVMProps) {
  const dispatch = useAppDispatch()
  const userDevice = useAppSelector((state) => state.app.userDevice)
  const scheduleFilters = useAppSelector((state: RootState) => state.modal.scheduleFilters) as ScheduleFiltersState
  const loading = useAppSelector((state) => state.modal.loading)
  const filterNavigate = useAppSelector((state: RootState) => state.schedule.filterNavigate)

  const units = useAppSelector((state: RootState) => state.units.units)?.filter(
    (unit) =>
      unit.attributes?.User?.data?.id &&
      unit.attributes?.User?.data?.id === Number(process.env.REACT_APP_BSO_USER_ID ?? REACT_APP_BSO_USER_ID ?? '57') &&
      unit.attributes?.ManagedBy === 'BSO' &&
      Boolean(unit?.attributes?.Marketing)
  ) as Unit[] | undefined

  const [localFilters, setLocalFilters] = useState<ScheduleFiltersState>(scheduleFilters)

  useEffect(() => {
    setLocalFilters(scheduleFilters)
  }, [scheduleFilters])

  const listUnits = useMemo(() => {
    const safeUnits = units ?? []

    if (filterNavigate === 'offers') {
      return safeUnits.filter((unit) => unit.attributes?.Offers?.data && unit.attributes?.Offers?.data?.length > 0)
    }
    if (filterNavigate === 'viewings') {
      return safeUnits.filter((unit) => unit.attributes?.Viewings?.data && unit.attributes?.Viewings?.data?.length > 0)
    }
    if (filterNavigate === 'adverts') {
      return safeUnits.filter((unit) => unit.attributes?.Advertises?.data && unit.attributes?.Advertises?.data?.length > 0)
    }

    return safeUnits
  }, [units, filterNavigate])

  const applyFilters = useCallback(() => {
    dispatch(setScheduleFilters(localFilters))
    dispatch(setShowPopupScheduleFilters(false))
    if (!scheduleFiltersApply) {
      setScheduleFiltersApply(true)
    }
  }, [dispatch, localFilters, scheduleFiltersApply, setScheduleFiltersApply])

  const resetFilters = useCallback(() => {
    setLocalFilters(DEFAULT_SCHEDULE_FILTERS)
    dispatch(setScheduleFilters(DEFAULT_SCHEDULE_FILTERS))
    dispatch(setShowPopupScheduleFilters(false))
    if (!scheduleFiltersApply) {
      setScheduleFiltersApply(true)
    }
  }, [dispatch, scheduleFiltersApply, setScheduleFiltersApply])

  const closePopup = useCallback(() => {
    dispatch(setShowPopupScheduleFilters(false))
  }, [dispatch])

  return {
    userDevice,
    loading,
    filterNavigate,
    localFilters,
    setLocalFilters,
    listUnits,
    applyFilters,
    resetFilters,
    closePopup
  }
}
